import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, AlertTitle, Box, IconButton, Snackbar, Typography} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import {
  GridLayout,
  LiveKitRoom as LiveKitRoomBase,
  ParticipantTile as ParticipantTileBase,
  RoomAudioRenderer,
  useLocalParticipant,
  useParticipants,
  useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import {ParticipantKind, Track} from 'livekit-client';
import {EntioCard} from 'src/components/ui';
import {neutral} from 'src/theme';
import message from 'src/components/Message';
import {attachFrontDeskCallPhoto} from 'src/apis/intercomOpsClient';
import {capturePhotoFromTrack, FrontDeskCallTarget, getResidentName} from './types';

const LiveKitRoom = LiveKitRoomBase as unknown as React.FC<any>;
const ParticipantTile = ParticipantTileBase as unknown as React.FC<any>;

const CALL_TIMEOUT_DEFAULT = 60;
const CALL_TIMEOUT_MIN = 10;
const CALL_TIMEOUT_MAX = 180;

function resolveTimeout(raw?: number) {
  const num = Number(raw);
  if (!Number.isFinite(num)) return CALL_TIMEOUT_DEFAULT;
  return Math.min(CALL_TIMEOUT_MAX, Math.max(CALL_TIMEOUT_MIN, Math.floor(num)));
}

function isParticipantAnswered(participant: {isLocal?: boolean; kind?: ParticipantKind; identity?: string}) {
  if (participant.isLocal) return false;
  const isSipKind = participant.kind === ParticipantKind.SIP;
  const isSipIdentity = (participant.identity || '').startsWith('sip:');
  return !isSipKind && !isSipIdentity;
}

function RoomContent({
  onAnswered,
  onRemoteLeft,
}: {
  onAnswered: () => void;
  onRemoteLeft: () => void;
}) {
  const tracks = useTracks(
    [
      {source: Track.Source.Camera, withPlaceholder: true},
      {source: Track.Source.ScreenShare, withPlaceholder: false},
    ],
    {onlySubscribed: false}
  );
  const participants = useParticipants();
  const [hasHadAnsweredRemote, setHasHadAnsweredRemote] = useState(false);

  useEffect(() => {
    const answeredRemotes = participants.filter(isParticipantAnswered);

    if (hasHadAnsweredRemote && answeredRemotes.length === 0) {
      const timer = window.setTimeout(() => onRemoteLeft(), 1000);
      return () => window.clearTimeout(timer);
    }

    if (answeredRemotes.length > 0) {
      setHasHadAnsweredRemote(true);
      onAnswered();
    }
  }, [hasHadAnsweredRemote, onAnswered, onRemoteLeft, participants]);

  return (
    <>
      <RoomAudioRenderer />
      <GridLayout tracks={tracks} style={{height: '100%'}}>
        <ParticipantTile />
      </GridLayout>
    </>
  );
}

function LocalCallSnapshot({operationLogId}: {operationLogId?: string}) {
  const cameraTracks = useTracks([{source: Track.Source.Camera, withPlaceholder: false}]);
  const uploadedRef = useRef(false);
  const localCameraTrack = cameraTracks.find(track => track.participant.isLocal)?.publication?.track
    ?.mediaStreamTrack;

  useEffect(() => {
    if (!operationLogId || uploadedRef.current || !localCameraTrack) return;

    let cancelled = false;
    void (async () => {
      const blob = await capturePhotoFromTrack(localCameraTrack);
      if (cancelled || !blob || uploadedRef.current) return;
      uploadedRef.current = true;
      try {
        await attachFrontDeskCallPhoto(operationLogId, blob);
      } catch (error) {
        uploadedRef.current = false;
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [localCameraTrack, operationLogId]);

  return null;
}

function CallControls({onEndCall}: {onEndCall: () => void}) {
  const {localParticipant} = useLocalParticipant();
  const [micOn, setMicOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);

  const toggleMic = async () => {
    if (!localParticipant) return;
    await localParticipant.setMicrophoneEnabled(!micOn);
    setMicOn(!micOn);
  };

  const toggleVideo = async () => {
    if (!localParticipant) return;
    await localParticipant.setCameraEnabled(!videoOn);
    setVideoOn(!videoOn);
  };

  const Control = ({
    label,
    children,
    onClick,
    danger,
  }: {
    label: string;
    children: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
  }) => (
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5}}>
      <IconButton onClick={onClick} sx={{bgcolor: danger ? 'error.main' : 'rgba(255,255,255,0.12)', color: '#fff'}}>
        {children}
      </IconButton>
      <Typography variant="caption" sx={{color: 'rgba(255,255,255,0.78)'}}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        mt: 1.5,
        py: 1.25,
        bgcolor: '#111827',
        borderRadius: 2,
      }}
    >
      <Control label={micOn ? 'Mute' : 'Unmute'} onClick={toggleMic} danger={!micOn}>
        {micOn ? <MicIcon /> : <MicOffIcon />}
      </Control>
      <Control label={videoOn ? 'Stop Video' : 'Start Video'} onClick={toggleVideo} danger={!videoOn}>
        {videoOn ? <VideocamIcon /> : <VideocamOffIcon />}
      </Control>
      <Control label="End Call" onClick={onEndCall} danger>
        <CallEndIcon />
      </Control>
    </Box>
  );
}

interface ResidentCallPanelProps {
  call: FrontDeskCallTarget | null;
  onEnd: () => void;
}

export default function ResidentCallPanel({call, onEnd}: ResidentCallPanelProps) {
  const [answered, setAnswered] = useState(false);
  const [connected, setConnected] = useState(false);
  const [remoteHungUpOpen, setRemoteHungUpOpen] = useState(false);

  useEffect(() => {
    setAnswered(false);
    setConnected(false);
  }, [call?.callData.meetRoomName]);

  useEffect(() => {
    if (!call || answered) return;
    const timeoutMs = resolveTimeout(call.timeoutSeconds) * 1000;
    const timer = window.setTimeout(() => {
      message.warning('No answer. The call was ended.');
      onEnd();
    }, timeoutMs);
    return () => window.clearTimeout(timer);
  }, [answered, call, onEnd]);

  const handleAnswered = useCallback(() => setAnswered(true), []);
  const handleRemoteLeft = useCallback(() => {
    setRemoteHungUpOpen(true);
    onEnd();
  }, [onEnd]);

  const name = call ? getResidentName(call.tenant) : '';
  const unit = call?.tenant.room?.name;
  const building = call?.tenant.building?.name;

  return (
    <>
    <EntioCard title="Resident Call (Web)">
      {!call ? (
        <Box
          sx={{
            minHeight: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: neutral[900],
            borderRadius: 2,
            px: 2,
          }}
        >
          <Typography variant="body2" sx={{color: 'rgba(255,255,255,0.72)', textAlign: 'center'}}>
            No active call. Select a resident and click Call.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {answered || connected ? 'Connected' : 'Calling...'}
          </Typography>
          <Typography variant="subtitle1">{name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
            {[unit && `Unit ${unit}`, building].filter(Boolean).join(' • ')}
          </Typography>
          <LiveKitRoom
            token={call.callData.jwtToken}
            serverUrl={call.callData.hostUrl}
            connect
            audio
            video
            onConnected={() => setConnected(true)}
            onDisconnected={onEnd}
            onError={() => {
              message.error('Unable to start the browser call.');
              onEnd();
            }}
          >
            <Box
              sx={{
                height: 220,
                bgcolor: '#111827',
                borderRadius: 2,
                overflow: 'hidden',
                '--lk-bg': '#111827',
              }}
            >
              <RoomContent onAnswered={handleAnswered} onRemoteLeft={handleRemoteLeft} />
            </Box>
            <LocalCallSnapshot operationLogId={call.callData.operationLogId} />
            <CallControls onEndCall={onEnd} />
          </LiveKitRoom>
        </>
        )}
      </EntioCard>

      <Snackbar
        open={remoteHungUpOpen}
        autoHideDuration={3000}
        onClose={() => setRemoteHungUpOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity="info"
          variant="outlined"
          onClose={() => setRemoteHungUpOpen(false)}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderColor: 'info.main',
            '& .MuiAlert-icon': {color: 'info.main'},
            '& .MuiAlert-message': {color: 'text.primary'},
            '& .MuiAlert-action': {color: 'text.secondary'},
          }}
        >
          <AlertTitle sx={{color: 'text.primary', fontWeight: 600, mb: 0.25}}>
            Call ended
          </AlertTitle>
          The other party has hung up.
        </Alert>
      </Snackbar>
    </>
  );
}
